// src/hooks/useOptimizedProfile.ts
// Hook yang dioptimasi untuk menggantikan useProfileData dengan caching yang lebih baik

import { useState, useEffect } from 'react';
import { 
  useOptimizedProfileDataService,
  useOptimizedAchievementsService,
  useOptimizedGalleryService,
  useOptimizedEducationService,
  useOptimizedCertificatesService
} from '../services/optimizedDataService';
import { useCourses } from './useOptimizedProfileData';

// Hook untuk data profil lengkap dengan caching
export const useOptimizedProfile = (profileId?: string) => {
  const { data: profileData, loading: profileLoading, error: profileError } = useOptimizedProfileDataService(profileId);
  
  return {
    profileData,
    loading: profileLoading,
    error: profileError
  };
};

// Hook untuk achievements dengan caching
export const useOptimizedAchievements = (profileId?: string) => {
  const { data: achievements, loading, error } = useOptimizedAchievementsService(profileId);
  
  return {
    achievements: achievements || [],
    loading,
    error
  };
};

// Hook untuk gallery dengan caching
export const useOptimizedGallery = (profileId?: string) => {
  const { data: gallery, loading, error } = useOptimizedGalleryService(profileId);
  
  return {
    gallery: gallery || [],
    loading,
    error
  };
};

// Hook untuk education dengan caching
export const useOptimizedEducation = () => {
  const { data: education, loading, error } = useOptimizedEducationService();
  
  return {
    education: education || [],
    loading,
    error
  };
};

// Hook untuk certificates dengan caching
export const useOptimizedCertificates = () => {
  const { data: certificates, loading, error } = useOptimizedCertificatesService();
  
  return {
    certificates: certificates || [],
    loading,
    error
  };
};

// Hook untuk courses dengan caching
export const useOptimizedCourses = () => {
  const { data: courseHistory, loading, error } = useCourses();
  
  return {
    courseHistory: courseHistory || [],
    loading,
    error
  };
};

// Hook gabungan untuk semua data profil dengan batching
export const useOptimizedProfileComplete = (profileId?: string) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const { profileData, loading: profileLoading, error: profileError } = useOptimizedProfile(profileId);
  const { achievements, loading: achievementsLoading, error: achievementsError } = useOptimizedAchievements(profileId);
  const { gallery, loading: galleryLoading, error: galleryError } = useOptimizedGallery(profileId);
  const { education, loading: educationLoading, error: educationError } = useOptimizedEducation();
  const { certificates, loading: certificatesLoading, error: certificatesError } = useOptimizedCertificates();
  const { courseHistory, loading: coursesLoading, error: coursesError } = useOptimizedCourses();
  
  const loading = profileLoading || achievementsLoading || galleryLoading || educationLoading || certificatesLoading || coursesLoading;
  const error = profileError || achievementsError || galleryError || educationError || certificatesError || coursesError;
  
  useEffect(() => {
    if (!loading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);
  
  return {
    profileData,
    achievements,
    gallery,
    education,
    certificates,
    courseHistory,
    loading: isInitialLoad ? loading : false, // Only show loading on initial load
    error,
    isInitialLoad
  };
};

// Hook untuk backward compatibility dengan useProfileData
export const useProfileData = (profileId?: string) => {
  const { profileData, loading, error } = useOptimizedProfile(profileId);
  
  return {
    profileData,
    loading,
    error,
    refetch: () => {
      // Implementasi refetch jika diperlukan
      window.location.reload();
    }
  };
};

// Hook untuk preloading data di background
export const useDataPreloader = () => {
  // Preload hooks dipanggil di top level untuk mengikuti Rules of Hooks
  useOptimizedProfileDataService();
  useOptimizedAchievementsService();
  useOptimizedGalleryService();
  useOptimizedEducationService();
  useOptimizedCertificatesService();
  
  // Return empty object untuk konsistensi
  return {};
};

// Export default untuk kemudahan import
export default useOptimizedProfile;